<template>
    <section class="filter-block blogList-block pt-lg-100 pb-lg-50 pt-md-80 pb-md-40 pt-sm-60 pb-sm-30 pt-50 pb-25" id="filter-block">
        <div class="container">
            <header class="filter-block__header">
                <h1 v-html="resourcesCategorySelected.title == 'All' ? 'Resources' : 'Resources'"></h1>
                <div v-if="resourcesCategorySelected.title == 'All'" v-html="pageDetailsArray.heroIntroText"></div>
                <div v-else v-html="resourcesCategorySelected.heroIntroText"></div>
            </header>
            
            <!-- Desktop Filters -->
            <div class="filter-banner">
                <div class="filter-banner__section">
                    <ul class="resource-cats">
                        <li v-for="item in resourcesCategoryArray" :key="item.slug" class="resource-cat" :class="{ 'is-active': (JSON.stringify(resourcesCategorySelected).includes(item.slug)) }">
                            <input
                                type="radio" 
                                :id="item.slug" 
                                :value="item" 
                                v-model="resourcesCategorySelected"
                                @change="runFilter(); getCategoryCounts();"
                                class="visuallyhidden"
                            >
                            <label :for="item.slug">
                                <div class="image-wrapper">
                                    <img :src="item.mainIcon || '/build/images/icon-all.svg'" :alt="item.title">
                                </div>
                                <span v-html="item.title === 'News' ? 'Latest News' : item.title"></span>
                            </label>
                        </li>
                        <li class="resource-cat">
                            <input type="radio" class="visuallyhidden">
                            <a href="https://seatingmatters.thinkific.com/products/courses/onlineacademy" target="_blank">
                            <label>
                                <div class="image-wrapper">
                                    <img src="/build/images/icon-academy.svg" alt="Online Academy">
                                </div>
                                <span>Online Academy</span>
                            </label>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="spinning-loading">
                <div class="loader"></div>
            </div>

            <!-- No Results Found -->
            <div v-else-if="!results.length" class="no-results">
                <span class="no-results__icon"></span>
                <div>
                    <h4>Sorry, no results found</h4>
                    <p>Please try again using different filter options.</p>
                    <button v-if="hasActiveFilters" class="reset-filter button button--black" @click="resetFilter">Clear All</button>
                </div>
            </div>

            <!-- Resources Cards -->
            <div v-else>
                <div class="grid blogList-block-grid">
                    <div v-for="(item, index) in results.slice(0, 6)" class="blogList-block-item"  :key="item.id">
                        <ResourceCard :resource="item"></ResourceCard>
                    </div>

                    <BookEnquiryBanner v-if="results.length >= 6"></BookEnquiryBanner>

                    <div v-for="(item, index) in results.slice(6, results.length)" class="blogList-block-item"  :key="item.id">
                        <ResourceCard :resource="item"></ResourceCard>
                    </div>
                </div>
                <Pagination :pagination="pagination" @set-page="setPage" />
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useResourceStore } from '../../stores/resource';
import Pagination from '../_shared/Pagination.vue';
import ResourceCard from './ResourceCard.vue';
import BookEnquiryBanner from './BookEnquiryBanner.vue';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const resourceStore = useResourceStore();

const firstLoad = ref(true);
const page = ref(route.query.page ?? 1);
const resourcesCategoryArray = ref(resourcesCategory || []);
const resourcesSubCategoryArray = ref(resourcesSubCategory || []);
const resourcesCategorySelected = ref({ slug: 'all', title: 'All Resources' });
const resourcesSubCategorySelected = ref([]);
const pageDetailsArray = ref(pageDetails || []);

const results = computed(() => resourceStore.results);
const pagination = computed(() => resourceStore.pagination);
const loading = computed(() => resourceStore.loading);

const hasActiveFilters = computed(() => resourcesSubCategorySelected.value.length);
const resourcePath = computed(() => `${route.path.includes('gb-ie') ? '/gb-ie' : ''}/resources${(resourcesCategorySelected.value.slug && resourcesCategorySelected.value.slug != 'all') ? `/${resourcesCategorySelected.value.slug}` : ''}`);

const initSelectedCategories = () => {
    if (route.params.resourceCategory) {
        resourcesCategorySelected.value = resourcesCategoryArray.value.find(rc => rc.slug === route.params.resourceCategory);
    }

    if (route.query.category) {
        processQueryStringOnCategory(resourcesSubCategorySelected.value, resourcesSubCategoryArray.value, route.query.category);
    }
    getCategoryCounts();
};

const ajaxRequest = () => {
    resourceStore.getResources(resourcesCategorySelected.value, route.query);
};

const runFilter = (pageNumber = 1) => {
    page.value = pageNumber;

    const payload = createPayload();

    if (firstLoad.value) {
        performInitialLoad();
    } else {
        updateRouterQuery(payload);
    }
};

const createPayload = () => {
    const payload = {};

    if (resourcesSubCategorySelected.value.length > 0) {
        payload.category = prepareQueryStringFromSelectedCategories(resourcesSubCategorySelected.value);
    }

    if (page.value !== 1) {
        payload.page = page.value;
    }

    return payload;
};

const performInitialLoad = () => {
    firstLoad.value = false;
    ajaxRequest();
};

const updateRouterQuery = (payload) => {
    router.push({ 
        path: resourcePath.value,
        query: payload
    });
};

const processQueryStringOnCategory = (categoriesSelected, sourceCategory, query) => {
    const itemInCategory = Array.isArray(query) ? query : [query];
    
    itemInCategory
        .map(element => sourceCategory.find(x => x.slug === element))
        .filter(foundCategory => foundCategory)
        .forEach(foundCategory => categoriesSelected.push(foundCategory));
};

const prepareQueryStringFromSelectedCategories = (categoryParams) => {
    return categoryParams.map(element => element.slug);
};

const setPage = (pageNumber) => {
    runFilter(pageNumber);
    const targetElement = document.getElementById("resources");

    if (targetElement) {
        targetElement.scrollIntoView({
            behavior: "smooth"
        });
    }
};

const resetFilter = () => {
    resourcesCategorySelected.value = { slug: '', title: 'All Resources' };
    resourcesSubCategorySelected.value = [];
    runFilter();
};

const getCategoryCounts = async () => {
    try {
        const payload = { resourcesCategoryArray: resourcesCategorySelected.value.slug };

        const { data } = await axios.get('/categories-with-entry-count.json', { params: payload });

        for (const category of data.data) {
            const resourceCategory = resourcesSubCategoryArray.value.find(rc => rc.slug === category.slug);
            if (resourceCategory) {
                resourceCategory.count = category.count;
            }
        }
    } catch (error) {
        console.error("There was an error fetching the category counts:", error);
    }
};

watch(() => route.query, ajaxRequest);

onMounted(() => {
    initSelectedCategories();
    runFilter(page.value);
});

</script>
